import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Cookies from "js-cookie";

export default function PredictorOtpLoginModal({
  isOpen,
  mobile,
  onClose,
  onSuccess,
}) {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0); // 5 minute OTP expiry
  const [resendTimer, setResendTimer] = useState(0); // 60 second resend cooldown
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const otpRefs = useRef([]);

  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  const isSharedNeetSupportDomain =
    typeof window !== "undefined" && window.location.hostname.endsWith("neetsupport.com");
  const isSecureContext = typeof window !== "undefined" && window.location.protocol === "https:";
  const cookieDomain = isSharedNeetSupportDomain ? ".neetsupport.com" : undefined;
  const cookieOptions = {
    path: "/",
    secure: isSecureContext,
    sameSite: isSecureContext ? "None" : "Lax",
    domain: cookieDomain,
  };

  useEffect(() => {
    if (!isOpen) {
      setOtp(["", "", "", ""]);
      setIsSending(false);
      setIsVerifying(false);
      setOtpSent(false);
      setTimer(0);
      setResendTimer(0);
      setIsOtpExpired(false);
    }
  }, [isOpen]);

  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setIsOtpExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => Math.max(prev - 1, 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (isOpen && mobile && mobile.length === 10 && !otpSent) {
      handleSendOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mobile]);

  // Auto-submit when OTP is complete
  useEffect(() => {
    const otpFilled = otp.join("").length === 4;
    if (otpFilled && otpSent && !isVerifying && !isOtpExpired) {
      const timer = setTimeout(() => {
        if (otp.join("").length === 4) {
          handleVerifyOtp();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [otp, otpSent, isVerifying, isOtpExpired]);

  const handleSendOtp = async () => {
    if (!mobile || mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${apiUrl}/auth/requestOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mobile }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setOtpSent(true);
        setTimer(300); // 5 minutes OTP expiry
        setResendTimer(60); // 60 seconds resend cooldown
        setOtp(["", "", "", ""]);
        toast.success(data?.message || "OTP sent successfully.");
        return;
      }

      if (data?.redirectTo) {
        toast.info("You are not registered. Please sign up.");
        window.location.href = data.redirectTo;
        return;
      }

      throw new Error(data?.message || "Error sending OTP.");
    } catch (error) {
      toast.error(error?.message || "Error sending OTP.");
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const nextOtp = [...otp];
    nextOtp[index] = value;
    setOtp(nextOtp);

    // Auto-focus to next field
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pastedData.length > 0) {
      const newOtp = pastedData.split("").concat(Array(4 - pastedData.length).fill(""));
      setOtp(newOtp.slice(0, 4));
      if (pastedData.length === 4) {
        setTimeout(() => otpRefs.current[3]?.focus(), 0);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleVerifyOtp = async () => {
    if (isOtpExpired) {
      toast.error("OTP has expired. Please request a new OTP.");
      return;
    }

    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP.");
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch(`${apiUrl}/auth/verifyOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mobile: `+91${mobile}`, otp: otpValue }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.token) {
        throw new Error(data?.message || "OTP verification failed.");
      }

      Cookies.set("authToken", data.token, cookieOptions);
      if (data.userId) Cookies.set("userId", data.userId, cookieOptions);
      if (data.name) Cookies.set("userName", data.name, cookieOptions);
      if (data.userType) Cookies.set("userType", data.userType, cookieOptions);

      toast.success(data.message || "Login successful.");
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
      onClose();
    } catch (error) {
      toast.error(error?.message || "OTP verification failed.");
    } finally {
      setIsVerifying(false);
    }
  };

  if (!isOpen) return null;

  const otpFilled = otp.join("").length === 4;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center sm:p-0">
      <div className="relative w-full max-w-sm rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-2xl sm:p-8">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center text-2xl text-gray-400 transition hover:text-gray-600 sm:right-4 sm:top-4"
          aria-label="Close modal"
        >
          ×
        </button>

        {/* Header */}
        <div className="mb-2 pr-6">
          <h3 className="text-2xl font-bold text-[#1A374D] sm:text-2xl">
            Verify Your Mobile
          </h3>
        </div>

        {/* Phone Number Display */}
        <p className="mb-6 text-sm text-gray-600 sm:text-base">
          We sent a 4-digit OTP to <span className="font-semibold text-gray-800">+91{mobile}</span>
        </p>

        {/* OTP Input Fields */}
        <div className="mb-8">
          <label className="mb-3 block text-sm font-medium text-gray-700">
            Enter OTP
          </label>
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                maxLength={1}
                inputMode="numeric"
                autoComplete="off"
                aria-label={`OTP digit ${index + 1}`}
                disabled={isOtpExpired}
                className="h-14 rounded-xl border-2 border-gray-200 text-center text-2xl font-bold text-[#1A374D] transition placeholder-gray-400 focus:border-[#ff9245] focus:outline-none focus:ring-2 focus:ring-[#ff9245]/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 sm:h-16"
                placeholder="•"
              />
            ))}
          </div>
          {isOtpExpired && (
            <p className="mt-2 text-sm font-medium text-red-500">
              OTP has expired. Please request a new one.
            </p>
          )}
        </div>

        {/* Verify Button */}
        <button
          type="button"
          onClick={handleVerifyOtp}
          disabled={isVerifying || !otpFilled || isOtpExpired}
          className="mb-3 w-full rounded-full bg-gradient-to-r from-[#ff9245] to-[#ff7f1f] px-6 py-3 font-semibold text-white shadow-md transition duration-300 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 sm:py-3.5"
        >
          {isVerifying ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
              Verifying...
            </span>
          ) : (
            "Verify OTP"
          )}
        </button>

        {/* Resend OTP Button */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleSendOtp}
            disabled={isSending || (resendTimer > 0 && !isOtpExpired)}
            className="flex-1 rounded-full border-2 border-[#ff9245] px-6 py-3 font-semibold text-[#ff9245] transition duration-300 hover:bg-[#fff7ef] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSending ? (
              <span className="flex items-center justify-center gap-2">
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#ff9245] border-r-transparent" />
                Sending...
              </span>
            ) : resendTimer > 0 && !isOtpExpired ? (
              `Resend in ${resendTimer}s`
            ) : (
              "Resend OTP"
            )}
          </button>
        </div>

        {/* Footer Text */}
        <p className="mt-4 text-center text-xs text-gray-500 sm:text-sm">
          {isOtpExpired ? (
            <span className="font-medium text-red-500">OTP expired. Please request a new one.</span>
          ) : timer > 0 ? (
            <>OTP expires in <span className="font-semibold text-[#ff9245]">{formatTime(timer)}</span></>
          ) : (
            "OTP expires in 5 minutes"
          )}
        </p>
      </div>
    </div>
  );
};